import { ConfigurationProduct, StockProduct } from '@dh-io-pls/pls-ui-types';
import { OVERSEAS_MARKETS } from '@sccs/common';
import { ConfigurationProductProductGroup, IFcisWidgetParameters, IVehicle } from '@sccs/types';
import { convertStringToDcpProductTypeEnum } from '../../util/StringToDcpProductType';
import { FCIS_CALLER_ID, FCIS_CALLER_ID_CONFIGURATION } from '../../util/config/GlobalConstant';

type BasePriceData = {
  dcpProductType?: string;
  market: string;
  locale: string;
  currency?: string;
  priceRawValue?: number;
  changeYear?: string;
};

type PriceDataStock = BasePriceData & {
  productCategory?: string;
  productCode?: string;
  commissionNumber?: string;
  configurationId?: string;
  productType?: 'STOCK';
};

type PriceDataConfiguration = BasePriceData & {
  productGroup?: ConfigurationProductProductGroup;
  vehicleId?: string;
  baumuster?: string;
  modelYear?: string;
  nst?: string;
  productType: 'CONFIGURATION';
};

type PriceDataUsed = BasePriceData & {
  usedCarId?: string;
  productType: 'USED';
};

type PriceData = PriceDataStock | PriceDataConfiguration | PriceDataUsed;

const productTypeHandlers = {
  CONFIGURATION: (priceData: PriceDataConfiguration) => ({
    vehicleId: priceData.vehicleId ?? 'no vehicleId',
    productGroup: priceData.productGroup ?? 'PC',
  }),
  STOCK: (priceData: PriceDataStock, dcpProductType?: ReturnType<typeof convertStringToDcpProductTypeEnum>) => ({
    code: priceData.productCode ?? 'no code',
    ...(dcpProductType && {
      dcpProductType: { name: dcpProductType },
    }),
    productCategory: priceData.productCategory,
  }),
  USED: (priceData: PriceDataUsed, dcpProductType?: ReturnType<typeof convertStringToDcpProductTypeEnum>) => ({
    code: priceData.usedCarId ?? 'no code',
    ...(dcpProductType && {
      dcpProductType: { name: dcpProductType },
    }),
    badges: [],
    isUsed: true,
  }),
};

const createPlsProduct = (priceData: PriceData) => {
  const dcpProductType = priceData.dcpProductType
    ? convertStringToDcpProductTypeEnum(priceData.dcpProductType)
    : undefined;
  switch (priceData.productType) {
    case 'CONFIGURATION':
      return productTypeHandlers.CONFIGURATION(priceData);
    case 'STOCK':
      return productTypeHandlers.STOCK(priceData, dcpProductType);
    case 'USED':
      return productTypeHandlers.USED(priceData, dcpProductType);
    default:
      throw new Error(`Unsupported product type: ${priceData.productType}`);
  }
};

export const generatePrices = (
  priceData: PriceData,
): { plsProduct: ConfigurationProduct | StockProduct; fcisWidgetParameters: IFcisWidgetParameters } => {
  const plsProduct: StockProduct | ConfigurationProduct = createPlsProduct(priceData);
  // compute base fcisWidgetParameter values
  const caller = priceData.productType === 'CONFIGURATION' ? FCIS_CALLER_ID_CONFIGURATION : FCIS_CALLER_ID; // marketplace callerId not working for configurations
  const locale = `${priceData.locale}-${priceData.market}`; // not an error. fcis really wants the marketId as locale
  const market = priceData.market;
  const productCode = (() => {
    switch (priceData.productType) {
      case 'CONFIGURATION':
        return priceData.vehicleId;
      case 'STOCK':
        return priceData.productCode;
      case 'USED':
        return priceData.usedCarId;
      default:
        throw new Error(`Unsupported product type for productCode in fcisWidgetParameter: ${priceData.productType}`);
    }
  })();
  // create base fcisWidgetParameters object
  const fcisWidgetParameters: IFcisWidgetParameters = {
    caller,
    locale,
    market,
    productCode,
  };

  // create base vehicle object
  const vehicle = {} as IVehicle;

  // add change year if it is set
  if (priceData.changeYear) {
    vehicle.vehicleConfiguration = {
      changeYear: priceData.changeYear,
    };
  }

  // add additional values for configuration vehicles
  if (priceData.productType === 'CONFIGURATION') {
    vehicle.condition = {
      condition: 'new',
    };
    vehicle.vehicleConfiguration = {
      ...vehicle.vehicleConfiguration,
      division: getFcisDivision(priceData.productGroup),
      baumuster: priceData.baumuster,
      modelYear: priceData.modelYear,
      nst: priceData.nst,
    };
  }

  if (priceData.productType === 'USED') {
    vehicle.condition = {
      condition: 'used',
    };
    fcisWidgetParameters.customer = {};
    fcisWidgetParameters.vehicleId = priceData.usedCarId; // Not an error, fcis really wants the usedCarId as vehicleId additionally
    fcisWidgetParameters.caller = 'ucos';
  }

  // add additional values for stock vehicles of overseas markets
  if (priceData.productType === 'STOCK' && OVERSEAS_MARKETS.includes(priceData.market)) {
    const technicalIdReference = priceData.productCode?.split('_')?.pop();
    vehicle.condition = {
      condition: 'new',
    };
    vehicle.technicalIdReference = technicalIdReference;
    vehicle.prices = [
      {
        id: 'purchasePrice',
        currency: priceData.currency,
        rawValue: priceData.priceRawValue,
      },
    ];
    fcisWidgetParameters.customer = {};
  }

  // add commission number or configurationId as vehicleId for non-overseas stock vehicles
  if (priceData.productType === 'STOCK' && !OVERSEAS_MARKETS.includes(priceData.market)) {
    fcisWidgetParameters.vehicleId = priceData.configurationId ?? priceData.commissionNumber; // not an error. fcis really wants everything but a vehicleId as vehicleId
  }

  // add vehicle to fcisWidgetParameters if the vehicle object is not empty
  if (Object.keys(vehicle).length > 0) {
    fcisWidgetParameters.vehicle = vehicle;
  }

  return {
    plsProduct,
    fcisWidgetParameters,
  };
};

const getFcisDivision = (productGroup?: ConfigurationProductProductGroup): string | undefined => {
  switch (productGroup) {
    case 'PC':
      return 'pc';
    case 'VAN':
    case 'VAN_PRIVATE':
      return 'van';
    case 'VAN_COMMERCIAL':
      return 'cv';
    default:
      return undefined;
  }
};
